import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import classnames from 'classnames';

import {
  SR_API_ACTION_FRONT_GET_CONVERSATION_MESSAGES,
  SR_API_ACTION_FRONT_GET_CONVERSATION_COMMENTS,
} from 'api/actionTypes';

import { makeConversationTimeline } from 'common/formatters';

import postWithState from 'client/actions/postWithState';

import ActivityIndicator from 'client/components/ActivityIndicator';

const SrFrontConversationView = forwardRef(
  ({ user, frontConversationId, onLoaded, dispatch }, ref) => {
    const [inProgress, setInProgress] = useState(true);
    const [frontMessages, setFrontMessages] = useState([]);
    const [frontComments, setFrontComments] = useState([]);

    const _loadData = useCallback(() => {
      Promise.all([
        dispatch(postWithState, {
          action: SR_API_ACTION_FRONT_GET_CONVERSATION_MESSAGES,
          payload: {
            frontConversationId,
            count: 100,
          },
        }),
        dispatch(postWithState, {
          action: SR_API_ACTION_FRONT_GET_CONVERSATION_COMMENTS,
          payload: {
            frontConversationId,
          },
        }),
      ]).then(([frontMessages, frontComments]) => {
        if (!frontMessages.length) {
          setInProgress(false);
          if (onLoaded) {
            onLoaded();
          }
          return;
        }

        const lastFrontMessageCreatedAt = frontMessages[frontMessages.length - 1].created_at * 1000;

        setFrontMessages(frontMessages);
        setFrontComments(
          frontComments.filter(
            (frontComment) => frontComment.posted_at * 1000 > lastFrontMessageCreatedAt,
          ),
        );
        setInProgress(false);

        if (onLoaded) {
          onLoaded();
        }

        const element = document.getElementById('bottom');
        element.scrollIntoView();
      });
    }, [dispatch, frontConversationId, onLoaded]);

    useImperativeHandle(
      ref,
      () => {
        return {
          addMessage: (frontMessage) => {
            setFrontMessages([...frontMessages, frontMessage]);

            const element = document.getElementById('bottom');
            element.scrollIntoView();
          },
        };
      },
      [frontMessages],
    );

    useEffect(() => {
      _loadData();
    }, [_loadData]);

    const timeline = makeConversationTimeline({
      userName: user.name,
      frontMessages,
      frontComments,
    });

    if (inProgress) {
      return <ActivityIndicator />;
    }

    if (!frontMessages.length) {
      return null;
    }

    return (
      <div className="SrFrontConversationView">
        {timeline.map((activity, activityIndex) => {
          const previousActivity = timeline[activityIndex - 1] || {};
          const isSameAuthor =
            previousActivity.author === activity.author &&
            previousActivity.isComment === activity.isComment;
          return (
            <div
              key={activity.id}
              className={classnames('messageContainer', {
                message: activity.isMessage,
                comment: activity.isComment,
                isSameAuthor: isSameAuthor,
                isInbound: activity.isInbound,
              })}
            >
              {!isSameAuthor && (
                <div className="header">
                  <span className="author">
                    {activity.author}
                    {activity.isInbound && <span className="phone">{activity.phone}</span>}
                  </span>
                  <span className="createdAt">{activity.createdAtDate}</span>
                </div>
              )}
              <div className="textContainer">
                {isSameAuthor && <span className="createdAt">{activity.createdAtDate}</span>}
                {activity.type === 'message' ? (
                  <span>{activity.text}</span>
                ) : (
                  <div className="bubble">
                    {activity.text.split('\n').map((text, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <span key={i}>{text}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div id="bottom" />
      </div>
    );
  },
);

SrFrontConversationView.displayName = 'SrFrontConversationView';

export default SrFrontConversationView;
